import { BillingScheduleContextData } from 'Cube/domain'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import { CubeStatus } from 'modules/Cube/domain/cube.domain.types'
import { useCallback, useMemo, useState } from 'react'
import { Location, useNavigate } from 'react-router-dom'

export enum BillingScheduleModals {
  ConfirmStart = 'confirmStart',
  Archive = 'archive',
  SteppedConfirmStart = 'steppedConfirmStart'
}

type UseBillingScheduleHeader = () => {
  scheduleId: string
  isNew: boolean
  status: CubeStatus
  title: string
  isArchived: boolean
  save: {
    show: boolean
    disabled: boolean
    onClick: () => void
    text: string
  }
  saveDraft: {
    show: boolean
    disabled: boolean
    onClick: () => void
    text: string
  }
  cancel: {
    onClick: () => void
  }
  blocker: {
    enabled: boolean
    ignoreBlock: (nextLocation: Location, currentLocation?: Location) => boolean
    submitting: boolean
  }
  modals: {
    confirmStart: {
      active: boolean
      onClose: () => void
      onConfirm: () => Promise<void>
      submitting: boolean
    }
    steppedConfirmStart: {
      active: boolean
      onClose: () => void
      onConfirm: () => Promise<void>
      submitting: boolean
      billingScheduleQueries: BillingScheduleContextData['queries']
    }
  }
  activateModal: (modal: BillingScheduleModals) => void
  onArchive: () => void
}

export const useBillingScheduleHeader: UseBillingScheduleHeader = () => {
  const cubeContext = useCubeContext()
  const navigate = useNavigate()
  const [activeModal, setActiveModal] = useState<BillingScheduleModals | null>(
    null
  )
  const [submitting, setSubmitting] = useState<boolean>(false)

  const activateModal = useCallback((modal: BillingScheduleModals) => {
    setActiveModal(modal)
  }, [])

  const title = useMemo(() => {
    if (cubeContext.queries.rawData.data.common.isArchived) {
      return 'Archived schedule'
    }
    if (
      cubeContext.queries.rawData.data.common.status ===
      CubeStatus.ScheduleUnsaved
    ) {
      return 'Create a billing schedule'
    }

    return 'Billing schedule'
  }, [cubeContext.queries.rawData.data.common])

  const isArchived = useMemo(
    () => cubeContext.queries.rawData.data.common.isArchived,
    [cubeContext.queries.rawData.data.common.isArchived]
  )

  const isNew = useMemo(
    () =>
      cubeContext.queries.rawData.data.common.status ===
      CubeStatus.ScheduleUnsaved,
    [cubeContext.queries.rawData.data.common.status]
  )

  const scheduleId = useMemo(
    () => cubeContext.queries.rawData.data.common.id,
    [cubeContext.queries.rawData.data.common.id]
  )

  const saveText = useMemo(() => {
    if (
      [CubeStatus.ScheduleActive, CubeStatus.SchedulePending].includes(
        cubeContext.queries.rawData.data.common.status
      )
    ) {
      return 'Update schedule'
    }
    return 'Start schedule'
  }, [cubeContext.queries.rawData.data.common.status])

  const saveDraftText = useMemo(() => {
    if (
      cubeContext.queries.rawData.data.common.status ===
      CubeStatus.ScheduleDraft
    ) {
      return 'Update draft'
    }
    return 'Save draft'
  }, [cubeContext.queries.rawData.data.common.status])

  const onSave = useCallback(async () => {
    if (cubeContext.queries.validation.validationErrorsPresent.publish) {
      cubeContext.mutators.updateEditor({
        activeValidationSet: 'publish'
      })
      return
    }

    if (
      [CubeStatus.ScheduleDraft, CubeStatus.ScheduleUnsaved].includes(
        cubeContext.queries.rawData.data.common.status
      )
    ) {
      if (
        cubeContext.queries.rawData.configuration.features.showNewTaxManagement
      ) {
        activateModal(BillingScheduleModals.SteppedConfirmStart)
      } else {
        activateModal(BillingScheduleModals.ConfirmStart)
      }

      return
    }

    const res = await cubeContext.mutators.external.out.schedule?.activate()

    if (res?.success) {
      navigate(
        `/billing-schedules/${cubeContext.queries.rawData.data.common.id}`
      )
    }
  }, [cubeContext, activateModal, navigate])

  const onSaveDraft = useCallback(async () => {
    if (cubeContext.queries.validation.validationErrorsPresent.save) {
      cubeContext.mutators.updateEditor({
        activeValidationSet: 'save'
      })
      return
    }
    const res = await cubeContext.mutators.external.out.save()

    if (res.id) {
      navigate(`/billing-schedules/${res.id}/edit`)
    }
  }, [cubeContext, navigate])

  const onCancel = useCallback(() => {
    const { id, status } = cubeContext.queries.rawData.data.common

    if (id && status !== CubeStatus.ScheduleUnsaved) {
      navigate(
        status === CubeStatus.ScheduleDraft
          ? '/billing-schedules'
          : `/billing-schedules/${id}`
      )
      return
    }

    navigate('/billing-schedules')
  }, [
    navigate,
    cubeContext.queries.rawData.data.common.id,
    cubeContext.queries.rawData.data.common.status
  ])

  const saveDraftDisabled = useMemo(() => {
    return !cubeContext.queries.availableFeatures.schedule.save.available
      .enabled
  }, [cubeContext])

  const saveDisabled = useMemo(() => {
    return !cubeContext.queries.availableFeatures.schedule.publish.available
      .enabled
  }, [cubeContext])

  const blocker = useMemo(() => {
    return {
      enabled: !saveDisabled && !saveDraftDisabled,
      submitting:
        cubeContext.queries.rawData.editor.savingDraft ||
        cubeContext.queries.rawData.editor.savingSchedule,
      ignoreBlock: ({ pathname }: Location, currentLocation?: Location) => {
        if (
          currentLocation?.pathname?.startsWith(
            '/billing-schedules/new/price-editor'
          )
        ) {
          return true
        }

        if (
          cubeContext.queries.rawData.data.common.status ===
          CubeStatus.ScheduleUnsaved
        ) {
          return (
            pathname.startsWith(`/billing-schedules/new/`) ||
            pathname.startsWith(`/cube/schedules/new/`)
          )
        }

        return (
          pathname.startsWith(
            `/billing-schedules/${cubeContext.queries.rawData.data.common.id}`
          ) ||
          pathname.startsWith(
            `/cube/schedules/${cubeContext.queries.rawData.data.common.id}`
          )
        )
      }
    }
  }, [
    saveDisabled,
    saveDraftDisabled,
    cubeContext.queries.rawData.data.common,
    cubeContext.queries.rawData.editor.savingDraft,
    cubeContext.queries.rawData.editor.savingSchedule
  ])

  const modals = useMemo(() => {
    return {
      confirmStart: {
        active: activeModal === BillingScheduleModals.ConfirmStart,
        onClose: () => setActiveModal(null),
        onConfirm: async () => {
          setSubmitting(true)
          const result =
            await cubeContext.mutators.external.out.schedule?.activate()
          setSubmitting(false)
          setActiveModal(null)
          if (result?.success) {
            navigate(`/billing-schedules/${result.id}`)
          }
        },
        submitting
      },
      steppedConfirmStart: {
        active: activeModal === BillingScheduleModals.SteppedConfirmStart,
        onClose: () => {
          setActiveModal(null)
        },
        onConfirm: async () => {
          setSubmitting(true)
          const result =
            await cubeContext.mutators.external.out.schedule?.activate()
          setSubmitting(false)
          setActiveModal(null)
          if (result?.success) {
            navigate(`/billing-schedules/${result.id}`)
          }
        },
        submitting,
        billingScheduleQueries: cubeContext.queries
      }
    }
  }, [activeModal, cubeContext, submitting, navigate])

  return {
    scheduleId,
    isNew,
    status:
      cubeContext.queries.rawData.data.common.status ===
      CubeStatus.ScheduleUnsaved
        ? CubeStatus.ScheduleUnsaved
        : cubeContext.queries.rawData.data.common.status,
    title,
    save: {
      show: cubeContext.queries.availableFeatures.schedule.publish.available
        .visible,
      disabled: saveDisabled,
      onClick: onSave,
      text: saveText
    },
    saveDraft: {
      show: cubeContext.queries.availableFeatures.schedule.save.available
        .visible,
      disabled: saveDraftDisabled,
      onClick: onSaveDraft,
      text: saveDraftText
    },
    cancel: {
      onClick: onCancel
    },
    isArchived,
    onArchive: () => {
      void cubeContext.mutators.external.in.core()
    },
    blocker,
    modals,
    activateModal
  }
}
